/**
 * Light / dark, with the system as the default.
 *
 * The tokens copied from the CRM console key off a `data-theme` attribute and do
 * NOT respond to prefers-color-scheme on their own -- that console owns its
 * theme through an explicit toggle, and inside /embed the attribute is set on
 * the host element rather than <html>. Bringing the stylesheet without this
 * module leaves the app permanently light, which on a console people read at
 * 3am is a real defect rather than a preference.
 */
export type Theme = 'light' | 'dark' | 'system';

const KEY = 'watch.theme';

export const storedTheme = (): Theme => {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    // A private window or blocked site data must not stop the app rendering.
    return 'system';
  }
};

const resolve = (theme: Theme): 'light' | 'dark' =>
  theme === 'system'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
    : theme;

export const applyTheme = (theme: Theme) => {
  document.documentElement.setAttribute('data-theme', resolve(theme));
  try {
    if (theme === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    /* ignore */
  }
};

export const initTheme = () => {
  applyTheme(storedTheme());
  // Follow the OS while the user has not chosen: a machine that flips to dark at
  // sunset should take the console with it.
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (storedTheme() === 'system') applyTheme('system');
  });
};
